import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Skeleton from "../components/Skeleton";
import { getUserNotifications } from "../services/notificationService";

const pageSize = 10;

const normalizeNotifications = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const notificationId = (notification, index) =>
  notification.id || notification._id || notification.notificationId || `${notification.title || "notification"}-${notification.createdAt || index}`;

const notificationMessage = (notification) =>
  notification.message || notification.body || notification.description || notification.text || "";

const notificationTitle = (notification) => {
  if (notification.title || notification.subject) return notification.title || notification.subject;
  const type = String(notification.type || notification.notificationType || notification.notification_type || notification.data?.type || notification.payload?.type || "").toLowerCase();
  if (type.includes("chat") || type.includes("message")) return "New chat message";
  if (type.includes("kupat")) return "Kupat Holim approval request";
  if (type.includes("order")) return "Order notification";
  return "New notification";
};

const notificationDate = (notification) => {
  const value = notification.createdAt || notification.created_at || notification.sentAt || notification.sent_at || notification.updatedAt || notification.updated_at;
  if (!value || Number.isNaN(new Date(value).getTime())) return "Just now";
  return new Intl.DateTimeFormat("en", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
};

const isUnread = (notification) => {
  if (typeof notification.unread === "boolean") return notification.unread;
  if (typeof notification.isUnread === "boolean") return notification.isUnread;
  if (typeof notification.read === "boolean") return !notification.read;
  if (typeof notification.isRead === "boolean") return !notification.isRead;
  return !notification.readAt && !notification.read_at;
};

const readNotificationIdsStorageKey = "readAdminNotificationIds";

const loadReadNotificationIds = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(readNotificationIdsStorageKey) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const notificationTarget = (notification) => {
  const payload = notification.data || notification.payload || {};
  const type = String(notification.type || notification.notificationType || notification.notification_type || payload.type || "").toLowerCase();
  const message = notification.normalizedMessage || notification.messageData || payload.message || {};
  const conversationId = notification.conversationId || notification.conversation_id || payload.conversationId || payload.conversation_id || message.conversationId || message.conversation_id;
  if (conversationId || type.includes("chat") || type.includes("message")) return conversationId ? { kind: "chat", id: conversationId } : null;

  const request = notification.kupatHolimRequest || notification.request || payload.kupatHolimRequest || payload.request || {};
  const requestId = notification.kupatHolimRequestId || notification.kupat_holim_request_id || notification.requestId || notification.request_id || payload.kupatHolimRequestId || payload.requestId || request.id || request._id;
  if (requestId || type.includes("kupat")) return requestId ? { kind: "kupat", id: requestId } : null;

  const order = notification.order || notification.orderDetails || payload.order || payload.orderDetails || {};
  const orderId = notification.orderId || notification.order_id || notification.referenceId || notification.reference_id || payload.orderId || payload.order_id || order.id || order._id || order.orderId;
  return orderId ? { kind: "order", id: orderId } : null;
};

export default function AllNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [readNotificationIds, setReadNotificationIds] = useState(loadReadNotificationIds);
  const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleNotifications = notifications.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstVisibleNotification = notifications.length ? (currentPage - 1) * pageSize + 1 : 0;
  const lastVisibleNotification = Math.min(currentPage * pageSize, notifications.length);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setNotifications(normalizeNotifications(await getUserNotifications()).map((notification, index) => readNotificationIds.includes(String(notificationId(notification, index))) ? { ...notification, unread: false, isUnread: false, read: true, isRead: true } : notification));
      setPage(1);
    } catch (loadError) {
      setError(loadError.message || "Unable to load received notifications.");
    } finally {
      setLoading(false);
    }
  }, [readNotificationIds]);

  useEffect(() => {
    loadNotifications();
    const refresh = (event) => {
      if (event.detail?.path === "/all-notifications" && event.detail?.action === "refresh") loadNotifications();
    };
    window.addEventListener("admin-page-action", refresh);
    return () => window.removeEventListener("admin-page-action", refresh);
  }, [loadNotifications]);

  useEffect(() => {
    const addChatNotification = (event) => {
      const notification = event.detail;
      if (!notification) return;
      setNotifications((current) => [notification, ...current.filter((item, index) => notificationId(item, index) !== notificationId(notification, 0))]);
      setPage(1);
    };
    window.addEventListener("chat-notification", addChatNotification);
    return () => window.removeEventListener("chat-notification", addChatNotification);
  }, []);

  useEffect(() => {
    localStorage.setItem(readNotificationIdsStorageKey, JSON.stringify(readNotificationIds));
  }, [readNotificationIds]);

  const openNotification = (notification, index) => {
    const id = String(notificationId(notification, index));
    setNotifications((current) => current.map((item, itemIndex) => String(notificationId(item, itemIndex)) === id ? { ...item, unread: false, isUnread: false, read: true, isRead: true } : item));
    setReadNotificationIds((current) => current.includes(id) ? current : [...current, id]);

    const target = notificationTarget(notification);
    if (target?.kind === "chat") navigate(`/chat?conversation=${encodeURIComponent(target.id)}`);
    if (target?.kind === "order") navigate(`/dashboard?order=${encodeURIComponent(target.id)}`);
  };

  return <div className="main-wrapper users-shell table-workspace-shell"><div className="page-wrapper"><main className="page-content"><div className="card rounded-4"><div className="p-4">
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    <div className="responisve-table"><table width="100%" border="0" cellSpacing="0" cellPadding="0"><thead><tr><th>#</th><th>Received notification</th><th>Date</th><th>Status</th></tr></thead><tbody>
      {loading && [1, 2, 3].map((item) => <tr key={item}><td colSpan="4" className="py-4"><Skeleton width="100%" height="1.5rem" /></td></tr>)}
      {!loading && !error && notifications.length === 0 && <tr><td colSpan="4" className="text-center py-4 text-muted">No received notifications found.</td></tr>}
      {!loading && visibleNotifications.map((notification, index) => { const absoluteIndex = (currentPage - 1) * pageSize + index; return <tr key={notificationId(notification, absoluteIndex)} className="all-notifications-row" role="button" tabIndex={0} onClick={() => openNotification(notification, absoluteIndex)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openNotification(notification, absoluteIndex); } }}><td>{absoluteIndex + 1}</td><td><strong>{notificationTitle(notification)}</strong>{notificationMessage(notification) && <div className="text-muted small mt-1">{notificationMessage(notification)}</div>}</td><td>{notificationDate(notification)}</td><td><span className={`notification-status ${isUnread(notification) ? "is-unread" : "is-read"}`}>{isUnread(notification) ? "Unread" : "Read"}</span></td></tr>; })}
    </tbody></table></div>
    {!loading && notifications.length > 0 && <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-3"><p className="small text-muted mb-0">Showing {firstVisibleNotification}-{lastVisibleNotification} of {notifications.length}</p><div className="d-flex align-items-center gap-2"><button type="button" className="btn btn-outline-primary pagination-button" disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button><span className="small text-muted">Page {currentPage} of {totalPages}</span><button type="button" className="btn btn-outline-primary pagination-button" disabled={currentPage === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</button></div></div>}
  </div></div></main></div></div>;
}
